import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { SITE } from "@/lib/site";
import { useLang } from "@/lib/i18n";

const pad = (i) => String(i + 1).padStart(2, "0");

const Body = ({ section, testId }) => (
  <>
    {section.paragraphs.map((p, i) =>
      typeof p === "string" ? (
        <p key={i}>{p}</p>
      ) : (
        <ul key={i}>
          {p.list.map((li) => (
            <li key={li}>{li}</li>
          ))}
        </ul>
      )
    )}
    {section.contact && (
      <p>
        <strong className="text-navy">{SITE.name}</strong>
        <br />
        {section.address
          ? SITE.address.slice(1).map((line) => (
              <span key={line}>
                {line}
                <br />
              </span>
            ))
          : (
            <>
              {section.location}
              <br />
            </>
          )}
        <a href={`mailto:${SITE.email}`} data-testid={`${testId}-contact-email`}>
          {SITE.email}
        </a>
      </p>
    )}
  </>
);

export const LegalPage = ({ data, testId }) => {
  const { t } = useLang();
  return (
    <>
      <section className="border-b border-line" data-testid={`${testId}-intro`}>
        <div className="container-x pb-14 pt-20 sm:pb-20 sm:pt-28">
          <motion.p className="eyebrow" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <span className="mr-3 inline-block h-1.5 w-1.5 rounded-full bg-coral align-middle" />
            {data.eyebrow}
          </motion.p>
          <motion.h1
            className="mt-6 max-w-3xl text-4xl font-extrabold tracking-tight text-navy sm:text-5xl"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            {data.title}
          </motion.h1>
          <p className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-muted" data-testid={`${testId}-last-updated`}>
            {t.legal.lastUpdated} : {data.lastUpdated}
          </p>
        </div>
      </section>

      <section data-testid={`${testId}-content`}>
        <div className="container-x grid gap-12 py-16 lg:grid-cols-12 sm:py-24">
          <aside className="hidden lg:col-span-3 lg:block">
            <nav className="sticky top-28 space-y-3" data-testid={`${testId}-toc`}>
              {data.sections.map((s, i) => (
                <a key={s.id} href={`#${s.id}`} className="flex gap-3 text-sm text-muted transition-colors duration-300 hover:text-navy">
                  <span className="font-mono text-xs text-coral">{pad(i)}</span>
                  <span>{s.title}</span>
                </a>
              ))}
            </nav>
          </aside>
          <article className="prose-legal max-w-2xl lg:col-span-8 lg:col-start-5">
            {data.sections.map((s, i) => (
              <Reveal key={s.id} id={s.id} className="scroll-mt-28" data-testid={`${testId}-section-${s.id}`}>
                <h2 className="!mt-0 flex items-baseline gap-4">
                  <span className="font-mono text-sm font-normal text-coral">{pad(i)}</span>
                  {s.title}
                </h2>
                <Body section={s} testId={testId} />
                {i < data.sections.length - 1 && <hr className="my-12 border-line" />}
              </Reveal>
            ))}
          </article>
        </div>
      </section>
    </>
  );
};
